import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type ActionItemSource = 'meeting' | 'email' | 'manual';
export type ActionItemStatus = 'suggested' | 'approved' | 'rejected' | 'modified' | 'completed';
export type ActionReviewAction = 'approve' | 'reject' | 'modify';

export interface ActionItem {
  id: string;
  tenant_id: string;
  source: ActionItemSource;
  source_meeting_id: string | null;
  source_email_id: string | null;
  project_id: string | null;
  title: string;
  description: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  due_date: string | null;
  status: ActionItemStatus;
  raw_payload: Record<string, unknown> | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionItemEdits {
  title?: string;
  assignee_id?: string | null;
  assignee_name?: string | null;
  due_date?: string | null;
  project_id?: string | null;
}

interface SubmitReviewParams {
  id: string;
  currentStatus: ActionItemStatus;
  action: ActionReviewAction;
  notes: string;
  edits?: ActionItemEdits;
}

interface CreateManualParams {
  title: string;
  description?: string;
  project_id?: string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  due_date?: string | null;
}

// "Modify" is itself an approval: editing an AI-suggested item and saving
// transitions it straight to 'approved' (applying the edits at the same
// time), not to a separate terminal 'modified' bucket. raw_payload (the
// original AI extraction) is untouched either way. 'modified' remains a
// valid value in the status CHECK constraint/type union for historical
// rows, but no UI action writes it anymore.
const STATUS_BY_ACTION: Record<ActionReviewAction, ActionItemStatus> = {
  approve: 'approved',
  reject: 'rejected',
  modify: 'approved',
};

export const useActionItems = () => {
  const { user, tenantId } = useAuth();
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('action_items')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error) {
      console.error('Failed to load action items:', error);
    } else {
      setItems((data as ActionItem[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('action-items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'action_items' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  const submitReview = useCallback(
    async (params: SubmitReviewParams): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!user) return { ok: false, error: 'Not authenticated' };
      setSubmitting(true);

      const newStatus = STATUS_BY_ACTION[params.action];

      const { error } = await supabase
        .from('action_items')
        .update({
          status: newStatus,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: params.notes || null,
          ...(params.action === 'modify' && params.edits ? params.edits : {}),
        })
        .eq('id', params.id);

      setSubmitting(false);
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [user],
  );

  const createManual = useCallback(
    async (params: CreateManualParams): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!user) return { ok: false, error: 'Not authenticated' };
      setSubmitting(true);

      const { error } = await supabase.from('action_items').insert({
        source: 'manual',
        status: 'approved',
        title: params.title,
        description: params.description || null,
        project_id: params.project_id || null,
        assignee_id: params.assignee_id || null,
        assignee_name: params.assignee_name || null,
        due_date: params.due_date || null,
        tenant_id: tenantId ?? undefined,
      });

      setSubmitting(false);
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [user, tenantId],
  );

  return { items, loading, submitting, submitReview, createManual, reload: load };
};
